import { create } from 'zustand';

/**
 * Store for sharing report context with Bizy chat.
 * Reports can push their current state here, and Bizy will include it in the conversation context.
 */

export interface BizyUIConfig {
	/** Translation key for greeting (or literal string if no translation) */
	greeting: string;
	/** Translation key for introduction (or literal string if no translation) */
	introduction: string;
	/** Array of capability items to display */
	capabilities: Array<{
		icon: 'search' | 'lightbulb' | 'sparkles';
		title: string;
		description: string;
	}>;
	/** Translation key for call-to-action (or literal string if no translation) */
	cta: string;
	/** Optional: translation namespace to use (e.g., 'admin_bizy', 'one_on_ones') */
	translationNamespace?: string;
}

interface BizyReportContextStore {
	context: Record<string, string>;
	uiConfig?: BizyUIConfig;
	setContext: (context: Record<string, string>, uiConfig?: BizyUIConfig) => void;
	clearContext: () => void;
}

export const useBizyReportContext = create<BizyReportContextStore>((set) => ({
	context: {},
	uiConfig: undefined,
	setContext: (context, uiConfig) => set({ context, uiConfig }),
	clearContext: () => set({ context: {}, uiConfig: undefined }),
}));

/**
 * Hook for reports to push their context to Bizy.
 * Call this from your report component with the current state.
 *
 * @example
 * ```tsx
 * const { setReportContext } = useBizyReportContextSetter();
 *
 * useEffect(() => {
 *   setReportContext(
 *     {
 *       report_type: 'participation',
 *       start_date: format(startDate, 'yyyy-MM-dd'),
 *       end_date: format(endDate, 'yyyy-MM-dd'),
 *     },
 *     {
 *       translationNamespace: 'admin_bizy',
 *       greeting: 'participation.greeting',
 *       introduction: 'participation.introduction',
 *       capabilities: [...],
 *       cta: 'participation.cta',
 *     }
 *   );
 * }, [startDate, endDate]);
 * ```
 */
export const useBizyReportContextSetter = () => {
	const setContext = useBizyReportContext((state) => state.setContext);
	const clearContext = useBizyReportContext((state) => state.clearContext);

	return {
		setReportContext: setContext,
		clearReportContext: clearContext,
	};
};
